//
import { isDefined } from 'app/utils/utils';
import { graphql } from 'graphql/client';
import locationGetAddressQuery from 'graphql/common/locationGetAddressQuery';
import locationGetCoordinatesQuery from 'graphql/common/locationGetCoordinatesQuery';
import userUpdatePositionQuery from 'graphql/users/userUpdatePositionQuery';

const LOCATION_TRACKING_TIME = 30000;

/**
 *
 */
class GeoCoding {
    locationTimer;
    /**
     * fromLatLng - description
     *
     * @param  {type} lat    description
     * @param  {type} lng    description
     * @return {type}        description
     */
    fromLatLong = (lat, lng) => {
        if (!isDefined(lat) || !isDefined(lng) || isNaN(lat) || isNaN(lng) || !this.isValidLatitute(lat) || !this.isValidLongitute(lng)) {
            return Promise.reject(new Error('Provided coordinates are invalid'));
        }
        return graphql.query({
            query: locationGetAddressQuery,
            variables: { latitude: lat, longitude: lng },
            fetchPolicy: 'no-cache',
        });
    };

    /**
     * async fromAddress - description
     *
     * @param  {type} address description
     * @return {type}         description
     */
    fromAddress = address => {
        if (!address) {
            return Promise.reject(new Error('Provided address is invalid'));
        }

        if (typeof address !== 'string') {
            return Promise.reject(new Error('Provided address must be a string'));
        }

        if (address.trim() === '') {
            return Promise.reject(new Error('Provided address cannot be empty'));
        }

        return graphql.query({
            query: locationGetCoordinatesQuery,
            variables: { address },
            fetchPolicy: 'no-cache',
        });
    };

    options = {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
    };

    /**
     * show the user current position
     */
    getCurrentLocation = (onSuccess, onError) => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(onSuccess, onError, this.options);
        }
    };

    /**
     * This function will be used to
     * convert Degree Minutes Seconds
     * to decimal format like 24.44
     */
    convertDMSToDD = (degrees = 0, minutes = 0, seconds = 0, direction) => {
        let dd = (degrees || 0) + (minutes || 0) / 60 + (seconds || 0) / (60 * 60);
        if (direction === 'S' || direction === 'W') {
            dd = dd * -1;
        }
        return dd;
    };

    /**
     * This function will be used to convert
     * decimal format to degree minutes and
     * secods format
     */
    convertDDtoDMS = (name, dd) => {
        if (isNaN(dd)) {
            return { degrees: undefined, minutes: undefined, seconds: undefined };
        }
        const degrees = parseInt(dd, 10);
        const minutes = parseInt((dd - degrees) * 60, 10);
        // const seconds = parseInt((dd - degrees - minutes / 60) * 3600, 10);
        const seconds = (dd - degrees - minutes / 60) * 3600;
        let direction = '';
        if (name === 'latitude') direction = dd > 0 ? 'N' : 'S';
        if (name === 'longitude') direction = dd > 0 ? 'E' : 'W';
        return { degrees: Math.abs(degrees) || undefined, minutes: Math.abs(minutes) || undefined, seconds: Math.abs(seconds) || undefined, direction };
    };

    /**
     * It will take the address_components from
     * geo code response and convert it to the
     * Address object with all required fields
     */
    getAddress = (gAddress = []) => {
        const address = { province: '', city: '', country: '', code: '', line1: '', line2: '' };
        if (!gAddress?.length) return address;
        const { addressLine1: line1, addressLine2: line2, province, postalCode: code, city, country } = gAddress[0] || {};
        return { province, city, country, code, line1, line2 };
    };

    /**
     * This function will check whether the latitude is vlaid or not
     */
    isValidLatitute = latitude => {
        const patt = new RegExp('^(\\+|-)?(?:90(?:(?:\\.0{1,6})?)|(?:[0-9]|[1-8][0-9])(?:(?:\\.[0-9]{1,50})?))$');
        return patt.test(latitude.toString());
    };

    /**
     * This function will check whether the longitude is vlaid or not
     */
    isValidLongitute = longitude => {
        const patt = new RegExp(/^(\+|-)?(?:180(?:(?:\.0{1,6})?)|(?:[0-9]|[1-9][0-9]|1[0-7][0-9])(?:(?:\.[0-9]{1,50})?))$/);
        return patt.test(String(longitude));
    };

    isLocationSharingOn = () => {
        // $FlowFixMe
        return localStorage.getItem('userPositionTrackingEnabled') === 'true';
    };

    updateUserLocation = (callback = () => { }) => {
        if (this.isLocationSharingOn()) {
            // Call very first time
            this.getCurrentLocation(
                coordinates => {
                    this.onSuccessPosition(coordinates, callback);
                },
                error => {
                    this.onPositionError(error, callback);
                }
            );
            // Set an interval as per LOCATION_TRACKING_TIME
            this.locationTimer = setInterval(() => {
                return this.getCurrentLocation(
                    coordinates => {
                        this.onSuccessPosition(coordinates, callback);
                    },
                    error => {
                        this.onPositionError(error, callback);
                    }
                );
            }, LOCATION_TRACKING_TIME);
        }
    };

    onSuccessPosition = (position, callback) => {
        const latitude = position.coords.latitude + '';
        const longitude = position.coords.longitude + '';
        const lastUpdate = parseInt(localStorage.getItem('userPositionTrackingLastUpdate'));
        const shouldUpdate = Date.now() - lastUpdate >= LOCATION_TRACKING_TIME;
        if (this.isLocationSharingOn()) {
            if (!lastUpdate || shouldUpdate) {
                // $FlowFixMe
                localStorage.setItem('userPositionTrackingLastUpdate', Date.now());
                if (isDefined(latitude) && isDefined(longitude)) {
                    graphql
                        .query({
                            query: userUpdatePositionQuery,
                            variables: { latitude, longitude },
                            fetchPolicy: 'no-cache',
                        })
                        .then(callback);
                }
            }
        } else {
            clearInterval(this.locationTimer);
        }
    };

    onPositionError = (error, callback) => {
        clearInterval(this.locationTimer);
        callback(error);
    };

    calcDistance = (mk1, mk2) => {
        if (!mk1 || !mk2) {
            return null;
        }
        const R = 6371e3;
        var rlat1 = mk1.lat * (Math.PI / 180); // Convert degrees to radians
        var rlat2 = mk2.lat * (Math.PI / 180); // Convert degrees to radians
        var difflat = rlat2 - rlat1; // Radian difference (latitudes)
        var difflon = (mk2.lng - mk1.lng) * (Math.PI / 180); // Radian difference (longitudes)

        var d =
            2 *
            R *
            Math.asin(
                Math.sqrt(Math.sin(difflat / 2) * Math.sin(difflat / 2) + Math.cos(rlat1) * Math.cos(rlat2) * Math.sin(difflon / 2) * Math.sin(difflon / 2))
            );
        return Number(Number(d / 1000).toFixed(2));
    };
}

const Geocode = new GeoCoding();

export default Geocode;
